import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid
} from 'recharts';
import { useApi, api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './Dashboard.css';

function fmt(n) {
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency', currency: 'CAD', maximumFractionDigits: 0
  }).format(n || 0);
}

// Skeleton pour la grille KPI (loading state)
function DashboardSkeleton() {
  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <div className="skeleton skel-title" />
          <div className="skeleton skel-sub" />
        </div>
        <div className="skeleton skel-btn" />
      </div>
      <div className="kpi-grid">
        {[0,1,2,3].map(i => (
          <div key={i} className="kpi-card card rise-in" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="kpi-top">
              <div className="skeleton skel-icon" />
              <div className="skeleton skel-label" />
            </div>
            <div className="skeleton skel-value" />
            <div className="skeleton skel-sub-short" />
          </div>
        ))}
      </div>
      <div className="kpi-row-2">
        {[0,1,2].map(i => (
          <div key={i} className="kpi-card card">
            <div className="kpi-top">
              <div className="skeleton skel-icon" />
              <div className="skeleton skel-label" />
            </div>
            <div className="skeleton skel-value" />
            <div className="skeleton skel-sub-short" />
          </div>
        ))}
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, color, iconName, trend }) {
  return (
    <div className="kpi-card card rise-in">
      <div className="kpi-top">
        <span className="kpi-icon">
          <Icon name={iconName || 'analytics'} size={18} />
        </span>
        <span className="kpi-label">{label}</span>
      </div>
      <div className="kpi-value" style={color ? { color } : undefined}>
        {fmt(value)}
      </div>
      {sub && <div className="kpi-sub">{sub}</div>}
      {trend !== undefined && (
        <div className={`kpi-trend ${trend >= 0 ? 'positive' : 'negative'}`}>
          {trend >= 0 ? '↑' : '↓'} vs année précédente
        </div>
      )}
    </div>
  );
}

function KpiMtd({ label, value, color, iconName, variation_pct, mois_prec_label, children }) {
  return (
    <div className="kpi-card card rise-in">
      <div className="kpi-top">
        <span className="kpi-icon">
          <Icon name={iconName || 'analytics'} size={18} />
        </span>
        <span className="kpi-label">{label}</span>
      </div>
      <div className="kpi-value" style={color ? { color } : undefined}>
        {fmt(value)}
      </div>
      {variation_pct !== null && variation_pct !== undefined ? (
        <div className={`kpi-variation ${variation_pct >= 0 ? 'hausse' : 'baisse'}`}>
          <span>{variation_pct >= 0 ? '↑' : '↓'}</span>
          <span>{variation_pct >= 0 ? '+' : ''}{variation_pct}% vs {mois_prec_label}</span>
        </div>
      ) : (
        <div className="kpi-variation kpi-variation--neutre">Premier mois</div>
      )}
      {children}
    </div>
  );
}

function EncaisseCard({ encaisse_totale, encaisse_detail }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="kpi-card card kpi-card-clickable rise-in" ref={ref} onClick={() => setOpen(o => !o)}>
      <div className="kpi-top">
        <span className="kpi-icon kpi-icon--accent">
          <Icon name="encaisse" size={18} />
        </span>
        <span className="kpi-label muted">Encaisse totale</span>
      </div>
      <div className="kpi-value kpi-value--accent">
        {fmt(encaisse_totale)}
      </div>
      <div className="kpi-variation kpi-variation--neutre kpi-variation--hint">
        Cash + Comptes — cliquer pour détail
      </div>
      {open && (
        <div className="encaisse-popover" onClick={e => e.stopPropagation()}>
          <div className="encaisse-popover-header">
            <Icon name="encaisse" size={14} />
            <span>Détail encaisse</span>
          </div>
          {encaisse_detail?.map(d => (
            <div key={d.localisation} className="encaisse-ligne">
              <span className="muted">{d.localisation}</span>
              <span className={d.solde < 0 ? 'danger' : 'encaisse-montant-positif'}>{fmt(d.solde)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MatiereCard({ valeur_matieres, nb_alertes, navigate }) {
  return (
    <div
      className="kpi-card card kpi-card-clickable rise-in"
      onClick={() => navigate('/inventaire/matieres')}
    >
      <div className="kpi-top">
        <span className="kpi-icon kpi-icon--accent">
          <Icon name="epices" size={18} />
        </span>
        <span className="kpi-label muted">Inventaire matières</span>
      </div>
      <div className="kpi-value kpi-value--accent">
        {fmt(valeur_matieres)}
      </div>
      {nb_alertes > 0 ? (
        <div className="kpi-variation kpi-variation--alerte">
          <Icon name="attention" size={13} />
          {nb_alertes} item{nb_alertes > 1 ? 's' : ''} sous seuil
        </div>
      ) : (
        <div className="kpi-variation kpi-variation--ok">
          <Icon name="check" size={13} />
          Tout est OK
        </div>
      )}
    </div>
  );
}

// Mapping produit -> nom d'icône (clés dédiées dans Icon.jsx)
const PRODUIT_ICON = {
  saumon:       'saumon',
  cotes_levees: 'cotes_levees',
  porc:         'porc',
  saucisses:    'saucisses',
  brisket:      'brisket',
  burnt_ends:   'burnt_ends',
  sauce:        'sauce',
};

const PRODUIT_ROUTE = {
  saumon:      '/production/saumon',
  cotes_levees:'/production/ribs',
  porc:        '/production/porc',
  saucisses:   '/production/saucisses',
  brisket:     '/production/brisket',
  burnt_ends:  '/production/brisket',
  sauce:       '/production/sauce',
};

function BatchesEnCoursCard({ batches_en_cours, navigate }) {
  const nb = batches_en_cours?.length || 0;

  return (
    <div className="kpi-card card rise-in">
      <div className="kpi-top">
        <span className="kpi-icon">
          <Icon name="fumage" size={18} />
        </span>
        <span className="kpi-label">En production</span>
      </div>
      <div className="kpi-value" style={nb === 0 ? { color: 'var(--muted)' } : undefined}>
        {nb}
      </div>
      {nb === 0 ? (
        <div className="kpi-variation kpi-variation--neutre">Aucun batch actif</div>
      ) : (
        <div className="batches-en-cours-liste">
          {batches_en_cours.map((b, i) => (
            <div
              key={i}
              className="batch-en-cours-item"
              onClick={() => navigate(PRODUIT_ROUTE[b.produit] || '/')}
            >
              <span className="batch-en-cours-icon">
                <Icon name={PRODUIT_ICON[b.produit] || 'package'} size={13} />
              </span>
              <span>{b.nom}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const MOIS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

function parseMois(data = []) {
  return MOIS.map((label, i) => {
    const key = `${new Date().getFullYear()}-${String(i + 1).padStart(2, '0')}`;
    const found = data.find(d => d.mois === key);
    return { label, revenus: found?.total || 0 };
  });
}

const BADGE_STATUT = {
  en_cours: { label: 'En cours', cls: 'badge--warning' },
  finalise:  { label: 'Finalisé', cls: 'badge--success' },
};

const PRODUIT_LABEL = {
  saumon:      'Saumon Fumé',
  cotes_levees:'Côtes Levées',
  porc:        'Porc Effiloché',
  brisket:     'Brisket Effiloché',
  burnt_ends:  'Brisket Burnt Ends',
  saucisses:   'Saucisses Jalapeño Cheddar',
};

// ─── Section Performance Production ──────────────────────────────────────────

function SectionAnalytiqueProduction({ annee }) {
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    api.get(`/analytics/overview?annee=${annee}`)
      .then(setOverview)
      .catch(() => {});
  }, [annee]);

  if (!overview) return null;

  const { kpis, par_produit } = overview;
  const meilleur = par_produit?.find(p => p.produit === kpis.meilleur_produit_marge);
  const meilleureYield = par_produit?.reduce((best, p) => !best || p.yield_moyen > best.yield_moyen ? p : best, null);

  return (
    <div className="card dash-analytics-section rise-in">
      <div className="dash-analytics-header">
        <div className="dash-analytics-title-row">
          <span className="dash-analytics-icon">
            <Icon name="analytics" size={16} />
          </span>
          <h2 className="card-title">Performance production</h2>
        </div>
        <Link to="/analytics" className="btn btn-ghost btn--sm">
          Voir l'analytique
          <Icon name="chevron" size={13} />
        </Link>
      </div>
      <div className="dash-analytics-grid">
        <div className="dash-analytics-kpi dash-analytics-kpi--accent">
          <span className="muted">Marge brute moyenne</span>
          <strong className="dash-analytics-value gold">
            {kpis.marge_moyenne_globale?.toFixed(1)}%
          </strong>
          <span className="muted dash-analytics-hint">Tous produits — {annee}</span>
        </div>
        <div className="dash-analytics-kpi">
          <span className="muted">Meilleur yield</span>
          <strong className="dash-analytics-value gold">
            {meilleureYield ? `${meilleureYield.yield_moyen?.toFixed(1)}%` : '—'}
          </strong>
          <span className="muted dash-analytics-hint">
            {meilleureYield?.label || '—'}
          </span>
        </div>
        <div className="dash-analytics-kpi">
          <span className="muted">Meilleur produit (marge)</span>
          <strong className="dash-analytics-value gold">
            {meilleur ? `${meilleur.marge_brute_pct?.toFixed(1)}%` : '—'}
          </strong>
          <span className="muted dash-analytics-hint">
            {meilleur?.label || '—'}
          </span>
        </div>
        <div className="dash-analytics-kpi">
          <span className="muted">Coût mat. ce mois</span>
          <strong className="dash-analytics-value">
            {kpis.cout_matieres_ce_mois > 0 ? `${kpis.cout_matieres_ce_mois?.toFixed(0)} $` : '—'}
          </strong>
          <span className="muted dash-analytics-hint">
            {kpis.batches_ce_mois} batch(s) ce mois
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Composant tooltip Recharts personnalisé ──────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <span className="chart-tooltip-label">{label}</span>
      <span className="chart-tooltip-value">{fmt(payload[0]?.value)}</span>
    </div>
  );
}

// ─── Dashboard principal ──────────────────────────────────────────────────────
export default function Dashboard() {
  const { data, loading, error, refetch } = useApi('/dashboard');
  const navigate = useNavigate();

  if (loading) return <DashboardSkeleton />;

  if (error) return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h1 className="page-title">Tableau de bord</h1>
        </div>
      </div>
      <div className="error-inline" style={{ maxWidth: 480 }}>
        <Icon name="erreur" size={18} />
        <span>Erreur : {error}</span>
        <button className="btn btn-ghost btn--sm" onClick={refetch} style={{ marginLeft: 'auto' }}>
          Réessayer
        </button>
      </div>
    </div>
  );

  const { kpis, batches_recents, revenus_mois, stock_disponible, transactions_recentes, inventaire_par_produit } = data;
  const chartData = parseMois(revenus_mois);
  const beneficeColor = kpis.benefice_net >= 0 ? 'var(--success)' : 'var(--danger)';

  return (
    <div className="dashboard">

      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-title">Tableau de bord</h1>
          <p className="muted page-subtitle">Exercice fiscal {kpis.annee}</p>
        </div>
        <button className="btn btn-ghost btn--sm" onClick={refetch}>
          <Icon name="rafraichir" size={14} />
          Actualiser
        </button>
      </div>

      {/* ── Rangée 1 — KPIs annuels ── */}
      <section className="kpi-grid" aria-label="Indicateurs annuels">
        <KpiCard
          label="Revenus"
          value={kpis.revenus}
          sub={`FY${kpis.annee}`}
          color="var(--accent)"
          iconName="argent"
        />
        <KpiCard
          label="Dépenses"
          value={kpis.depenses}
          sub="Opérations + équipement"
          iconName="recu"
        />
        <KpiCard
          label="Bénéfice net"
          value={kpis.benefice_net}
          sub="Revenus − Dépenses"
          color={beneficeColor}
          iconName="courbe"
        />
        <KpiCard
          label="Inventaire disponible"
          value={kpis.valeur_inventaire}
          sub="Valeur de vente au prix courant"
          iconName="package"
        />
      </section>

      {/* ── Rangée 2 — KPIs du mois ── */}
      <section className="kpi-row-2" aria-label="Indicateurs du mois">
        <KpiMtd
          label={`Revenus — ${kpis.mois_label}`}
          value={kpis.revenus_mtd}
          color="var(--accent)"
          iconName="argent"
          variation_pct={kpis.variation_revenus_pct}
          mois_prec_label={kpis.mois_prec_label}
        />
        <KpiMtd
          label={`Dépenses — ${kpis.mois_label}`}
          value={kpis.depenses_mtd}
          iconName="recu"
          variation_pct={kpis.variation_depenses_pct}
          mois_prec_label={kpis.mois_prec_label}
        />
        <div className="kpi-card card rise-in">
          <div className="kpi-top">
            <span className="kpi-icon">
              <Icon name="panier" size={18} />
            </span>
            <span className="kpi-label">Commandes — {kpis.mois_label}</span>
          </div>
          <div className="kpi-value">
            {kpis.commandes_mois_nb} commande{kpis.commandes_mois_nb !== 1 ? 's' : ''}
          </div>
          <div className="kpi-variation kpi-variation--neutre">
            {fmt(kpis.commandes_mois_total)} de ventes
          </div>
        </div>
      </section>

      {/* ── Rangée 3 — Santé opérationnelle ── */}
      <section className="kpi-row-3" aria-label="Santé opérationnelle">
        <EncaisseCard
          encaisse_totale={kpis.encaisse_totale}
          encaisse_detail={kpis.encaisse_detail}
        />
        <MatiereCard
          valeur_matieres={kpis.valeur_matieres}
          nb_alertes={kpis.nb_matieres_alertes}
          navigate={navigate}
        />
        <BatchesEnCoursCard
          batches_en_cours={kpis.batches_en_cours}
          navigate={navigate}
        />
      </section>

      {/* ── Stock + Valeur inventaire ── */}
      <section className="dashboard-stock-row">
        <div className="card rise-in">
          <div className="card-header">
            <span className="card-header-icon">
              <Icon name="boutique" size={15} />
            </span>
            <h2 className="card-title">Stock disponible</h2>
          </div>
          {!stock_disponible?.length ? (
            <div className="empty-state">
              <Icon name="package" size={32} />
              <span className="empty-state__titre">Aucun produit en stock</span>
            </div>
          ) : (
            stock_disponible.map(s => (
              <div key={s.produit} className="stock-row">
                <span className="stock-row-label">{PRODUIT_LABEL[s.produit] || s.produit}</span>
                <span className="stock-row-qty gold">{s.total} unités</span>
              </div>
            ))
          )}
        </div>

        <div className="card rise-in" style={{ animationDelay: '60ms' }}>
          <div className="card-header">
            <span className="card-header-icon">
              <Icon name="analytics" size={15} />
            </span>
            <h2 className="card-title">Valeur inventaire</h2>
          </div>
          {!inventaire_par_produit?.length ? (
            <div className="empty-state">
              <Icon name="liste" size={32} />
              <span className="empty-state__titre">Aucune donnée d'inventaire</span>
            </div>
          ) : (
            <table className="esc-table">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Unités</th>
                  <th>Valeur</th>
                </tr>
              </thead>
              <tbody>
                {inventaire_par_produit.map(p => (
                  <tr key={p.produit}>
                    <td>{PRODUIT_LABEL[p.produit] || p.produit}</td>
                    <td>{p.total_unites}</td>
                    <td className="gold">{fmt(p.valeur_totale)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td><strong>TOTAL</strong></td>
                  <td></td>
                  <td className="gold"><strong>{fmt(kpis.valeur_inventaire)}</strong></td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* ── Graphique revenus ── */}
      <div className="card chart-card rise-in">
        <div className="card-header">
          <span className="card-header-icon">
            <Icon name="courbe" size={15} />
          </span>
          <h2 className="card-title">Revenus mensuels {kpis.annee}</h2>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="var(--accent)" stopOpacity={0.35}/>
                <stop offset="95%" stopColor="var(--accent)" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
            <XAxis dataKey="label" tick={{ fill: 'var(--muted)', fontSize: 12 }} axisLine={false} tickLine={false}/>
            <YAxis tick={{ fill: 'var(--muted)', fontSize: 11 }} axisLine={false} tickLine={false}
                   tickFormatter={v => v === 0 ? '' : `${v/1000}k`}/>
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="revenus" stroke="var(--accent)" strokeWidth={2}
                  fill="url(#goldGrad)" dot={false} activeDot={{ r: 4, fill: 'var(--accent)' }}/>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* ── Section Performance Production ── */}
      <SectionAnalytiqueProduction annee={kpis.annee} />

      {/* ── Bas de page : batches + transactions ── */}
      <section className="dashboard-bottom">
        <div className="card rise-in">
          <div className="card-header">
            <span className="card-header-icon">
              <Icon name="liste" size={15} />
            </span>
            <h2 className="card-title">Batches récents</h2>
          </div>
          {!batches_recents?.length ? (
            <div className="empty-state">
              <Icon name="fumage" size={32} />
              <span className="empty-state__titre">Aucun batch enregistré</span>
            </div>
          ) : (
            <table className="esc-table">
              <thead>
                <tr>
                  <th>Batch</th>
                  <th>Produit</th>
                  <th>Date</th>
                  <th>Statut</th>
                  <th>Unités</th>
                </tr>
              </thead>
              <tbody>
                {batches_recents.map(b => {
                  const badgeInfo = BADGE_STATUT[b.statut];
                  return (
                    <tr key={b.id}>
                      <td className="font-medium">{b.nom}</td>
                      <td className="muted">{PRODUIT_LABEL[b.produit] || b.produit}</td>
                      <td className="muted">{b.date_prod}</td>
                      <td>
                        <span className={`badge ${badgeInfo?.cls || ''}`}>
                          {badgeInfo?.label || b.statut}
                        </span>
                      </td>
                      <td className="gold">{b.nb_produits || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="card rise-in" style={{ animationDelay: '60ms' }}>
          <div className="card-header">
            <span className="card-header-icon">
              <Icon name="recu" size={15} />
            </span>
            <h2 className="card-title">Dernières transactions</h2>
          </div>
          {!transactions_recentes?.length ? (
            <div className="empty-state">
              <Icon name="argent" size={32} />
              <span className="empty-state__titre">Aucune transaction enregistrée</span>
            </div>
          ) : (
            <table className="esc-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Associé</th>
                  <th>Montant</th>
                </tr>
              </thead>
              <tbody>
                {transactions_recentes.map(t => (
                  <tr key={t.id}>
                    <td className="muted">{t.date_trans?.slice(0, 10)}</td>
                    <td>{t.description}</td>
                    <td><span className="badge badge--gold">{t.associe}</span></td>
                    <td className={t.montant >= 0 ? 'success' : 'danger'}>
                      {fmt(t.montant)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
